import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { mapPatientFilterField } from "../../../../api/filters/patientFilters";
import type { BackendFilters } from "../../../../utils/backendFilters";
import { formatDob } from "../../../../utils/dateUtils";
import DataTable, {
  type DataTableColDef,
  type PaginationModel,
  type SortModel,
  useServerFilterModel,
} from "../../../DataTable";
import { renderCellRedacted } from "../../../redact";
import CustomNoRowsOverlay from "../../../TableNoDataOverlay";
import PatientListHeader from "./PatientListHeader";

const PERSIST_KEY = "patientsListView";

const getColumns = (
  searchParams: URLSearchParams,
  practices: PracticeMessage[],
): DataTableColDef<PatientWithClaimMessage>[] => [
  {
    flex: 1,
    field: "name",
    headerName: "Patient Name",
    type: "string",
    renderCell: renderCellRedacted,
    valueGetter: (_value, row) => {
      const { wieldyFirstName, wieldyMiddleName, wieldyLastName } = row;
      const middle = wieldyMiddleName ? ` ${wieldyMiddleName}` : "";
      return (
        `${wieldyFirstName || ""}${middle} ${wieldyLastName || ""}`.trim() || ""
      );
    },
  },
  {
    flex: 1,
    field: "practice",
    headerName: "Practice",
    type: "string",
    sortable: false,
    filterable: false,
    renderCell: renderCellRedacted,
    valueGetter: (_value, row) =>
      practices.find((p) => p.wieldyId === row.practiceId)?.displayName ?? "",
  },
  {
    flex: 1,
    field: "dob",
    headerName: "Patient DOB",
    type: "string",
    renderCell: renderCellRedacted,
    valueGetter: (_value, row) => (row.dob ? formatDob(row.dob) : ""),
  },
  {
    flex: 0.5,
    field: "details",
    headerName: "Details",
    sortable: false,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    disableExport: true,
    renderCell: ({ row }) => (
      <Link to={`/patients/${row.wieldyId}?${searchParams}`}>
        <Button
          type="button"
          variant="outlined"
          style={{
            cursor: "pointer",
            width: "100%",
          }}
        >
          <NavigateNextIcon
            style={{ cursor: "pointer" }}
            aria-label="Select Row"
          />
        </Button>
      </Link>
    ),
  },
];

interface PatientsListViewProps {
  patients: PatientWithClaimMessage[];
  patientsLoading: boolean;
  practices: PracticeMessage[];
  pagination?: {
    page: number;
    pageSize: number;
    rowCount: number;
    totalPages: number;
  };
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSortChange?: (field: string | null, direction: "asc" | "desc") => void;
  onFilterChange?: (filters: BackendFilters) => void;
}

export default function PatientsListView({
  patients,
  patientsLoading,
  practices,
  pagination,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onFilterChange,
}: PatientsListViewProps) {
  const theme = useTheme();

  const searchParams = new URLSearchParams(window.location.search);

  const effectivePageSize = pagination?.pageSize ?? 10;
  const lastPageSize = useRef(effectivePageSize);

  const { filterModel, onFilterModelChange } = useServerFilterModel({
    persistKey: PERSIST_KEY,
    mapField: mapPatientFilterField,
    onFiltersChange: onFilterChange,
  });

  const [sortModel, setSortModel] = useState<SortModel>([]);

  const handleSortModelChange = useCallback(
    (model: SortModel) => {
      setSortModel(model);
      if (!onSortChange) return;
      if (model.length > 0) {
        onSortChange(model[0].field, model[0].sort);
      } else {
        onSortChange(null, "desc");
      }
    },
    [onSortChange],
  );

  const handlePaginationModelChange = useCallback(
    ({ page, pageSize }: PaginationModel) => {
      if (pageSize !== lastPageSize.current) {
        lastPageSize.current = pageSize;
        onPageSizeChange?.(pageSize);
      } else {
        onPageChange?.(page + 1);
      }
    },
    [onPageChange, onPageSizeChange],
  );

  return (
    <Box sx={{ paddingTop: theme.spacing(4) }}>
      <Box sx={{ marginBottom: theme.spacing(4) }}>
        <PatientListHeader practices={practices} />
      </Box>
      <DataTable
        loading={patientsLoading}
        persistKey={PERSIST_KEY}
        rows={patients}
        getRowId={(row) => row.wieldyId}
        columns={getColumns(searchParams, practices)}
        rowCount={pagination?.rowCount ?? patients.length}
        paginationModel={{
          page: (pagination?.page ?? 1) - 1,
          pageSize: effectivePageSize,
        }}
        pageSizeOptions={[10, 20, 40]}
        onPaginationModelChange={handlePaginationModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        noRowsOverlay={<CustomNoRowsOverlay />}
      />
    </Box>
  );
}
